import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { damFormServices } from "@proof/sdk";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Download,
  Save,
  Check,
  RefreshCw,
  Clock3,
  MessageSquare,
  Eye,
  FileCheck2,
  AlertTriangle,
  SlidersHorizontal,
} from "lucide-react";
import {
  FormGenerator,
  ProofCanvas,
  useDocumentScene,
  type AssetInfo,
} from "@proof/ui";
import {
  createScene,
  previewOutput,
  validateSubmission,
  resolve,
  uid,
  type Project,
  type Values,
  type OutputProfile,
  type Repository,
  type ProofRecord,
  applyFormProfile,
  effectiveFields,
  rebindPartValues,
  type MapProviderInfo,
  type MapFormServices,
  type MapSearchResult,
  type MapGenerated,
} from "@proof/core";
import { useHostContext } from "./useHostContext";
import { Select, Button, Control, Modal } from "./components";
import { type JobView } from "./api";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { RetainHandoff } from "./HandoffPackages";
import { ApiLink } from "./ApiLink";
import { GenerationConflictDialog } from "./GenerationConflictDialog";
import { NativeFormProfiles } from "./FormProfiles";
import { ProfileSwitcher } from "./ProfileSwitcher";
import { profileSelectors, type ProfileSelector } from "@proof/core";
export interface ProofProps {
  sourceParameters?: import("@proof/core").SourceParameters;
  onSourceParametersChange?: (
    values: import("@proof/core").SourceParameters,
  ) => void;
  project: Project;
  repository: Repository;
  versionId?: string;
  values: Values;
  onValues: (values: Values) => void;
  partId: string;
  onPart: (id: string) => void;
  notify: (s: string) => void;
  formPresentation?: "inline" | "modal";
  demoProfile?: "alex" | "jamie" | "guest";
  onDemoProfile?: (profile: "alex" | "jamie" | "guest") => void;
  customerPreview?: boolean;
}
export function Proof({
  sourceParameters,
  onSourceParametersChange,
  project: p,
  repository,
  values,
  onValues,
  partId,
  onPart,
  notify,
  versionId,
  formPresentation = "modal",
  demoProfile: selectedProfile,
  onDemoProfile,
  customerPreview = false,
}: ProofProps) {
  const transport = useWorkspaceTransport(),
    { api, upload, generate } = transport;
  const writableProof = transport.demo || !!versionId;
  const uploadImage = useCallback<
    NonNullable<React.ComponentProps<typeof FormGenerator>["upload"]>
  >(
    async (file, field, signal) => {
      const asset = await upload(file, field, signal, p.id);
      setAssets((current) => [...current, asset]);
      return asset;
    },
    [upload, p.id],
  );
  const [page, setPage] = useState(0),
    [zoomChoice, setZoom] = useState<number | "fit">("fit"),
    [artboardSize, setArtboardSize] = useState({ width: 0, height: 0 }),
    [outputId, setOutput] = useState(p.outputs[0].id),
    [comments, setComments] = useState(""),
    [initials, setInitials] = useState(""),
    [profileSelections, setProfileSelections] = useState<
      Record<string, string>
    >({}),
    [accepted, setAccepted] = useState(false),
    [validate, setValidate] = useState(false),
    [jobRecords, setJobRecords] = useState<
      { scope: string; value: JobView; profile: OutputProfile }[]
    >([]),
    [submitting, setSubmitting] = useState(false),
    [duplicate, setDuplicate] = useState<{ scope: string; value: JobView }>(),
    [assets, setAssets] = useState<AssetInfo[]>([]),
    [proofs, setProofs] = useState<ProofRecord[]>([]),
    [showSaved, setShowSaved] = useState(false),
    [showFull, setShowFull] = useState(false),
    [showProfile, setShowProfile] = useState<string>();
  const modalForm = formPresentation === "modal";
  const [showBleed, setShowBleed] = useState(
    !!p.settings.bleedPreview?.defaultVisible &&
      p.settings.bleedPreview?.enabled !== false,
  );
  useEffect(
    () =>
      setShowBleed(
        p.settings.bleedPreview?.enabled !== false &&
          !!p.settings.bleedPreview?.defaultVisible,
      ),
    [
      p.id,
      p.settings.bleedPreview?.enabled,
      p.settings.bleedPreview?.defaultVisible,
    ],
  );
  const [formOpen, setFormOpen] = useState(modalForm);
  const formOpener = useRef<HTMLButtonElement>(null);
  const [localProfile, setLocalProfile] = useState<"alex" | "jamie" | "guest">(
    "alex",
  );
  const demoProfile = selectedProfile ?? localProfile;
  const setDemoProfile = (profile: "alex" | "jamie" | "guest") => {
    setLocalProfile(profile);
    onDemoProfile?.(profile);
    // Selecting a profile always reloads its current values.
    host.refresh();
  };
  const closeForm = () => {
    setFormOpen(false);
  };
  useEffect(() => {
    if (modalForm && !formOpen) formOpener.current?.focus();
  }, [modalForm, formOpen]);
  const needsMaps = effectiveFields(p, partId || undefined).some(
    (field) => field.type === "map",
  );
  const [mapProvider, setMapProvider] = useState<MapProviderInfo | null>();
  useEffect(() => {
    setMapProvider(undefined);
    if (!needsMaps) return;
    const controller = new AbortController();
    void api<{ provider: MapProviderInfo | null }>("/maps/provider", {
      signal: controller.signal,
    })
      .then(({ provider }) => {
        if (!controller.signal.aborted) setMapProvider(provider);
      })
      .catch(() => {
        if (!controller.signal.aborted) setMapProvider(null);
      });
    return () => controller.abort();
  }, [needsMaps]);
  const maps = useMemo<MapFormServices | undefined>(
    () =>
      mapProvider
        ? {
            notice: mapProvider.notice,
            provider: mapProvider,
            search: (query, signal) =>
              api<MapSearchResult>("/maps/search", {
                method: "POST",
                body: JSON.stringify({ query }),
                signal,
              }),
            create: async (location, providerId, field, signal) => {
              const generated = await api<MapGenerated>("/maps/create", {
                method: "POST",
                body: JSON.stringify({
                  ...transport.projectInput(p, versionId),
                  partId: partId || undefined,
                  fieldId: field.id,
                  location,
                  providerId,
                }),
                signal,
              });
              setAssets((current) => [
                ...current.filter((asset) => asset.id !== generated.asset.id),
                generated.asset,
              ]);
              return generated;
            },
          }
        : undefined,
    [p, partId, mapProvider],
  );
  const dam = useMemo(
    () => damFormServices(transport.client, p, partId || undefined, versionId),
    [p, partId],
  );
  const host = useHostContext(
      p,
      partId,
      demoProfile,
      true,
      versionId,
      sourceParameters,
      onSourceParametersChange,
    ),
    {
      context,
      blocked: contextBlocked,
      needs: needsContext,
      current: currentContext,
    } = host;
  const output = p.outputs.find((o) => o.id === outputId) || p.outputs[0];
  // Show bleed previews the real bleed settings: the selected output's
  // production, else the project's print output that defines one, else the
  // page's own bleed.
  const hasBleed = (production?: { bleed?: unknown } | null) => {
    const bleed = production?.bleed;
    return typeof bleed === "number"
      ? bleed > 0
      : !!bleed &&
          typeof bleed === "object" &&
          Object.values(bleed).some((edge) => Number(edge) > 0);
  };
  const bleedSource =
    hasBleed(output.production) ||
    p.pages.some((page) => hasBleed(page.production))
      ? output
      : p.outputs.find((candidate) => hasBleed(candidate.production));
  const bleedConfigured = !!bleedSource;
  const previewProfile = useMemo(
    () =>
      previewOutput(
        showBleed && bleedSource && bleedSource !== output
          ? { ...output, production: bleedSource.production }
          : output,
        showBleed,
      ),
    [output, showBleed, bleedSource],
  );
  const renderKey = JSON.stringify([
    p,
    values,
    partId,
    outputId,
    demoProfile,
    host.key,
    currentContext?.hash,
  ]);
  const renderScope = useRef(renderKey);
  renderScope.current = renderKey;
  const queueScope = JSON.stringify([p.id, versionId, demoProfile]);
  const queueScopeRef = useRef(queueScope);
  queueScopeRef.current = queueScope;
  const submittingRef = useRef(false);
  const queueLifetime = useRef(0);
  useEffect(() => {
    queueLifetime.current++;
    setJobRecords([]);
    setDuplicate(undefined);
    setSubmitting(false);
    submittingRef.current = false;
    return () => {
      queueLifetime.current++;
    };
  }, [queueScope, transport]);
  const updateJob = (value: JobView) =>
    setJobRecords((rows) =>
      rows.map((row) =>
        row.value.id === value.id &&
        !(
          ["complete", "cancelled", "failed"].includes(row.value.state) &&
          ["queued", "running"].includes(value.state)
        ) &&
        row.value.stateVersion <= value.stateVersion
          ? { ...row, value }
          : row,
      ),
    );
  useEffect(() => {
    setAccepted(false);
    setDuplicate(undefined);
  }, [renderKey]);
  const r = resolve(p, values, partId || undefined, context),
    proof = useDocumentScene(
      p,
      values,
      partId || undefined,
      context,
      previewProfile,
      transport.loadFont,
      showBleed,
    );
  const scene = proof.scene || {
    pages: p.pages.map((pg) => ({
      ...pg,
      width: pg.width || 612,
      height: pg.height || 792,
    })),
    diagnostics: [],
  };
  const part = p.parts.find((x) => x.id === partId);
  // "Fit" scales the live proof to the space the window leaves for it.
  const artboard = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = artboard.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) =>
      setArtboardSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      }),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  // The proofing page always fits the window: the workspace takes exactly the
  // height left below whatever banners sit above it.
  const workspace = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = workspace.current;
    if (!element || !modalForm) return;
    const fit = () => {
      if (matchMedia("(max-width: 900px)").matches) {
        element.style.height = "";
        return;
      }
      const top = element.getBoundingClientRect().top + window.scrollY;
      element.style.height = `${Math.max(480, window.innerHeight - top)}px`;
    };
    fit();
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(fit);
    observer?.observe(document.body);
    window.addEventListener("resize", fit);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [modalForm]);
  const shownPage = scene.pages[Math.min(page, scene.pages.length - 1)];
  const zoom =
    zoomChoice !== "fit"
      ? zoomChoice
      : artboardSize.width && artboardSize.height && shownPage
        ? Math.max(
            0.1,
            Math.min(
              artboardSize.width / shownPage.width,
              (artboardSize.height - 34) / shownPage.height,
            ),
          )
        : 0.9;
  const pendingIds = jobRecords
    .filter(
      ({ value }) => value.state === "queued" || value.state === "running",
    )
    .map(({ value }) => value.id)
    .sort()
    .join(",");
  useEffect(() => {
    let active = true;
    if (transport.can("tenant.assets.manage"))
      void api<AssetInfo[]>(`/assets?projectId=${encodeURIComponent(p.id)}`)
        .then((result) => {
          if (active) setAssets(result);
        })
        .catch(() => {});
    const refreshProofs = () =>
      void repository
        .proofs(p.tenantId, p.id)
        .then((result) => {
          if (active)
            setProofs(
              transport.demo
                ? result
                : result.filter((row) => row.versionId === versionId),
            );
        })
        .catch((error) => {
          if (active) notify((error as Error).message);
        });
    refreshProofs();
    window.addEventListener("storage", refreshProofs);
    window.addEventListener("focus", refreshProofs);
    return () => {
      active = false;
      window.removeEventListener("storage", refreshProofs);
      window.removeEventListener("focus", refreshProofs);
    };
  }, [p.id, repository, notify]);
  useEffect(() => {
    if (!pendingIds) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      await Promise.all(
        pendingIds.split(",").map(async (id) => {
          try {
            const next = await api<JobView>(`/jobs/${id}`, {
              signal: controller.signal,
            });
            if (!controller.signal.aborted) updateJob(next);
          } catch (error) {
            if (!controller.signal.aborted) notify((error as Error).message);
          }
        }),
      );
      if (!controller.signal.aborted)
        timer = setTimeout(() => void poll(), 700);
    };
    timer = setTimeout(() => void poll(), 700);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [pendingIds, queueScope, transport]);
  const submit = async (replaceJobId?: string) => {
    if (contextBlocked || submittingRef.current) return;
    setValidate(true);
    const valid = validateSubmission(p, values, partId || undefined, context);
    if (valid.errors.length) {
      if (modalForm) setFormOpen(true);
      notify(valid.errors[0].message);
      return;
    }
    const scope = renderKey,
      queue = queueScope,
      lifetime = queueLifetime.current;
    submittingRef.current = true;
    setSubmitting(true);
    setDuplicate(undefined);
    try {
      const generated = await generate(
        p,
        values,
        output,
        partId || undefined,
        {
          demoProfile,
          contextHash: currentContext?.hash,
          sourceParameters: host.sourceParameters,
          duplicatePolicy: "confirm",
          replaceJobId,
        },
        versionId,
      );
      if (queueScopeRef.current !== queue || lifetime !== queueLifetime.current)
        return;
      setJobRecords((rows) => {
        return [
          { scope, value: generated, profile: output },
          ...rows
            .filter((row) => row.value.id !== generated.id)
            .map((row) =>
              generated.replacesJobId === row.value.id
                ? {
                    ...row,
                    value: { ...row.value, state: "cancelled" as const },
                  }
                : row,
            ),
        ];
      });
      if (generated.duplicate && renderScope.current === scope)
        setDuplicate({ scope, value: generated });
    } catch (e) {
      if (lifetime === queueLifetime.current) notify((e as Error).message);
    } finally {
      if (lifetime === queueLifetime.current) {
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };
  const save = async () => {
    if (contextBlocked) return;
    setValidate(true);
    const valid = validateSubmission(p, values, partId || undefined, context);
    if (valid.errors.length) {
      if (modalForm) setFormOpen(true);
      notify(valid.errors[0].message);
      return;
    }
    if (accepted && !initials.trim()) {
      notify("Add your initials before saving an accepted proof.");
      return;
    }
    const record: ProofRecord = {
      id: uid("proof"),
      versionId,
      tenantId: p.tenantId,
      projectId: p.id,
      partId: partId || undefined,
      name: r.proofName || p.name,
      values: valid.snapshot,
      contextHash: currentContext?.hash,
      sourceParameters: host.sourceParameters,
      ...(Object.keys(profileSelections).length ? { profileSelections } : {}),
      comments,
      initials,
      accepted,
      at: new Date().toISOString(),
    };
    await repository.saveProof(p.tenantId, record);
    setProofs(
      (await repository.proofs(p.tenantId, p.id)).filter(
        (row) => transport.demo || row.versionId === versionId,
      ),
    );
    notify(
      transport.demo
        ? "Proof saved in this browser."
        : "Proof saved to workspace.",
    );
  };
  // Global profiles: one distinct profile → a single selector at the top;
  // several → one selector per section, each choosing independently.
  const selectors = profileSelectors(p);
  const switcher = (selector: ProfileSelector) => (
    <ProfileSwitcher
      key={`${p.id}:${selector.id}`}
      project={p}
      instanceId={selector.instanceIds[0]}
      instanceIds={selector.instanceIds}
      fieldIds={selector.fieldIds}
      label={`${selector.profileName} profile`}
      selection={profileSelections[selector.id]}
      onSelection={(value) =>
        setProfileSelections((current) => ({
          ...current,
          [selector.id]: value,
        }))
      }
      values={values}
      versionId={versionId}
      partId={partId || undefined}
      context={context}
      sourceParameters={sourceParameters}
      demoProfile={selectedProfile}
      disabled={contextBlocked}
      onValues={(next) => {
        onValues(next);
        setAccepted(false);
      }}
    />
  );
  const form = (
    <aside className="proof-input-panel">
      <div className="proof-input-heading">
        <span className="eyebrow">MAKE IT YOURS</span>
        {!modalForm && <h2>Personalize your document</h2>}
        <p>
          {part?.instructions ||
            p.instructions ||
            "Your changes appear in the proof as you type."}
        </p>
      </div>
      {p.parts.length > 0 && (
        <Control label="Project / part">
          <Select
            aria-label="Proof part"
            value={partId}
            onChange={(e) => {
              onValues(
                rebindPartValues(p, values, e.target.value || undefined),
              );
              onPart(e.target.value);
              setPage(0);
              setAccepted(false);
            }}
          >
            <option value="">Main project</option>
            {p.parts.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </Select>
        </Control>
      )}
      {(needsContext || modalForm) && (
        <div>
          {transport.demo && !customerPreview && (
            <Control label="Demo profile">
              <Select
                aria-label="Demo profile"
                value={demoProfile}
                onChange={(e) =>
                  setDemoProfile(e.target.value as typeof demoProfile)
                }
              >
                <option value="alex">Alex Morgan</option>
                <option value="jamie">Jamie Rivera</option>
                <option value="guest">Guest</option>
              </Select>
            </Control>
          )}
          {!needsContext && (
            <p className="muted">
              This document has no profile-bound fields. Personalize it with the
              form below.
            </p>
          )}

          {needsContext && !currentContext && (
            <p role="status">Loading profile values…</p>
          )}
          {currentContext?.error && <p role="alert">{currentContext.error}</p>}
        </div>
      )}
      {needsMaps && !mapProvider && (
        <p role="status">
          {mapProvider === null
            ? "Map generation is unavailable. You can upload an image instead."
            : "Loading map provider…"}
        </p>
      )}
      <FormGenerator
        sourceParameters={host.sourceParameters}
        onSourceParametersChange={host.setSourceParameters}
        loadAsset={transport.loadAsset}
        transformImage={transport.proofServices.transformImage}
        maps={maps}
        dam={dam}
        project={p}
        context={context}
        values={values}
        onChange={(v) => {
          onValues(v);
          setAccepted(false);
        }}
        partId={partId || undefined}
        compact={!modalForm}
        showValidation={validate}
        assets={assets}
        upload={uploadImage}
        header={
          selectors.top.length > 0 && (
            <div className="profile-switchers top">
              {selectors.top.map(switcher)}
            </div>
          )
        }
        sectionHeader={(groupId) => {
          const selector = selectors.sections.get(groupId);
          return selector ? (
            <div className="profile-switchers">{switcher(selector)}</div>
          ) : null;
        }}
      />
      {p.formInstances.some((instance) => instance.profileOnly) && (
        <details>
          <summary>Save or manage your profiles</summary>
          {p.formInstances
            .filter((instance) => instance.profileOnly)
            .map((instance) => (
              <Button
                key={instance.id}
                onClick={() => {
                  setFormOpen(false);
                  setShowProfile(instance.id);
                }}
              >
                {instance.label}
              </Button>
            ))}
        </details>
      )}
      {p.formInstances.some((instance) => !instance.profileOnly) && (
        <details>
          <summary>Save or manage reusable form profiles</summary>
          {p.formInstances
            .filter((instance) => !instance.profileOnly)
            .map((i) => (
              <Button
                key={i.id}
                onClick={() => {
                  setFormOpen(false);
                  setShowProfile(i.id);
                }}
              >
                {i.label}
              </Button>
            ))}
        </details>
      )}
      <div className="proof-form-actions">
        <button
          className="text-button"
          onClick={() => {
            onValues({});
            setAccepted(false);
            notify("Restored inherited defaults.");
          }}
        >
          <RefreshCw size={13} />
          Reset to defaults
        </button>
        <button
          className="text-button"
          onClick={() => {
            setFormOpen(false);
            setShowSaved(true);
          }}
        >
          <Clock3 size={13} />
          Saved proofs ({proofs.length})
        </button>
      </div>
    </aside>
  );
  return (
    <div
      ref={workspace}
      className={`proof-workspace ${modalForm ? "proof-modal-form" : ""} ${modalForm && formOpen ? "form-open" : ""}`}
    >
      {!modalForm && form}
      {modalForm && formOpen && (
        <div
          className="impersonation-form-modal"
          onKeyDown={(event) => {
            if (event.key !== "Tab") return;
            const controls = [
              ...event.currentTarget.querySelectorAll<HTMLElement>(
                'button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],[tabindex="0"]',
              ),
            ].filter((element) => element.getClientRects().length > 0);
            const target = event.shiftKey ? controls.at(-1) : controls[0];
            const boundary = event.shiftKey ? controls[0] : controls.at(-1);
            if (document.activeElement === boundary && target) {
              event.preventDefault();
              target.focus();
            }
          }}
        >
          <Modal title="Customize" onClose={closeForm} wide>
            {form}
            <div className="modal-actions">
              <Button kind="primary full" onClick={closeForm}>
                View proof
              </Button>
            </div>
          </Modal>
        </div>
      )}
      <aside className="proof-review-panel" inert={modalForm && formOpen}>
        {modalForm && (
          <button
            type="button"
            className="button primary full proof-customize"
            ref={formOpener}
            aria-haspopup="dialog"
            onClick={() => setFormOpen(true)}
          >
            <SlidersHorizontal size={15} /> Customize
          </button>
        )}
        <div>
          <span className="eyebrow">FINAL DETAILS</span>
          <h3>Review & export</h3>
        </div>
        <div className="review-summary">
          <div>
            <span>Document</span>
            <strong>{p.name}</strong>
          </div>
          <div>
            <span>Pages</span>
            <strong>{scene.pages.length}</strong>
          </div>
          <div>
            <span>Fields</span>
            <strong>{r.visible.size} visible</strong>
          </div>
          {r.price > 0 && (
            <div>
              <span>Options</span>
              <strong>
                {new Intl.NumberFormat(undefined, {
                  style: "currency",
                  currency: "USD",
                }).format(r.price)}
              </strong>
            </div>
          )}
        </div>
        <Control label="Output profile">
          <Select
            aria-label="Proof output profile"
            value={output.id}
            onChange={(e) => setOutput(e.target.value)}
          >
            {p.outputs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </Control>
        <div className="profile-badges">
          <span className="badge">{output.format.toUpperCase()}</span>
          <span className="badge">{output.dpi} DPI</span>
          <span className="badge">{output.colorSpace.toUpperCase()}</span>
        </div>
        {proof.error && (
          <p role="alert" className="error-text">
            {proof.error}
          </p>
        )}
        {scene.diagnostics.length > 0 && p.settings.overflowWarning && (
          <details className="warning-box">
            <summary>
              <AlertTriangle size={14} />
              {scene.diagnostics.length} layout warnings
            </summary>
            {scene.diagnostics.map((d, i) => (
              <p key={i}>{d.message}</p>
            ))}
          </details>
        )}
        {p.settings.comments && (
          <Control label="Proof comments">
            <textarea
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add a note for your team…"
            />
          </Control>
        )}
        <label className="approval-check">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span>I’ve reviewed this proof and approve the content.</span>
        </label>
        {accepted && (
          <Control label="Your initials">
            <input
              maxLength={10}
              value={initials}
              onChange={(e) => setInitials(e.target.value)}
            />
          </Control>
        )}
        <Button
          kind="primary full"
          disabled={
            submitting ||
            proof.loading ||
            !!proof.error ||
            contextBlocked ||
            !writableProof ||
            !transport.can("tenant.proofs.generate")
          }
          onClick={() => void submit()}
        >
          <Download size={16} />
          {submitting ? "Queuing…" : "Generate document"}
        </Button>
        <Button
          kind="full"
          disabled={contextBlocked || !writableProof}
          onClick={() =>
            void save().catch((error) => notify((error as Error).message))
          }
        >
          <Save size={15} />
          Save proof
        </Button>
        {!!jobRecords.length && <h3>Generation queue</h3>}
        {jobRecords.map(({ value: job, scope, profile }) => (
          <div
            key={job.id}
            className={`artifact-card ${job.state}`}
            aria-label={`${job.profileName} generation`}
          >
            <strong>{job.profileName}</strong>
            <small>
              {new Date(job.createdAt).toLocaleString()} | {job.id}
            </small>
            {scope !== renderKey && (
              <small className="muted">Earlier profile or form data</small>
            )}
            {job.replacesJobId && <small>Replaces {job.replacesJobId}</small>}
            <div className="artifact-status">
              {job.state === "complete" ? (
                <FileCheck2 size={19} />
              ) : (
                <Clock3 size={19} />
              )}
              <strong>
                {job.state === "complete"
                  ? "Your document is ready"
                  : job.state === "failed"
                    ? "Generation failed"
                    : job.state === "cancelled"
                      ? "Generation cancelled"
                      : job.state === "quarantined"
                        ? "Generation needs attention"
                        : job.state === "queued"
                          ? "Queued for generation"
                          : "Generating your document"}
              </strong>
            </div>
            {job.error && <p className="error-text">{job.error}</p>}
            {(job.state === "queued" || job.state === "running") && (
              <Button
                onClick={() =>
                  void api<JobView>(`/jobs/${job.id}/cancel`, {
                    method: "POST",
                    body: JSON.stringify({
                      stateVersion: job.stateVersion,
                      reason: "Cancelled from proofing screen",
                    }),
                  })
                    .then(updateJob)
                    .catch((error) => notify(error.message))
                }
              >
                Cancel generation
              </Button>
            )}
            {job.artifact && (
              <>
                <p>{job.artifact.filename}</p>
                <small>
                  {Math.round(job.artifact.bytes / 1024)} KB ·{" "}
                  {job.artifact.pageCount} page
                  {job.artifact.pageCount === 1 ? "" : "s"} ·{" "}
                  {job.artifact.colorSpace.toUpperCase()}
                </small>
                {job.artifact.sheetLayout && (
                  <p>
                    {job.artifact.sheetLayout.sheets} sheets ·{" "}
                    {job.artifact.sheetLayout.copies} copies per source ·{" "}
                    {job.artifact.sheetLayout.capacity} per full sheet
                    {job.artifact.sheetLayout.material && (
                      <>
                        {" "}
                        · {job.artifact.sheetLayout.material} ·{" "}
                        {job.artifact.sheetLayout.physicalSheets} physical
                        sheets
                      </>
                    )}
                    {job.artifact.sheetLayout.materialCost && (
                      <>
                        {" "}
                        ·{" "}
                        {job.artifact.sheetLayout.materialCost.amount.toFixed(
                          2,
                        )}{" "}
                        {job.artifact.sheetLayout.materialCost.currency}{" "}
                        material estimate
                      </>
                    )}
                  </p>
                )}
                <div className="artifact-links">
                  {job.artifact.downloadAllowed && (
                    <ApiLink
                      className="button primary"
                      path={`/jobs/${job.id}/artifact`}
                      filename={job.artifact.filename}
                      notify={notify}
                    >
                      <Download size={14} />
                      Download
                    </ApiLink>
                  )}
                  {job.state === "complete" && job.artifact.downloadAllowed && (
                    <>
                      <ApiLink
                        className="button"
                        path={`/jobs/${job.id}/variables.json`}
                        filename="variables.json"
                        reportErrors
                        notify={notify}
                      >
                        <Download size={14} /> Variables (JSON)
                      </ApiLink>
                      <ApiLink
                        className="button"
                        path={`/jobs/${job.id}/variables.xvp`}
                        filename="variables.xvp"
                        reportErrors
                        notify={notify}
                      >
                        <Download size={14} /> Variables (XVP)
                      </ApiLink>
                    </>
                  )}
                  {job.artifact.viewAllowed &&
                    job.artifact.mime !== "application/zip" && (
                      <ApiLink
                        className="button"
                        path={`/jobs/${job.id}/artifact?view=true`}
                        filename={job.artifact.filename}
                        notify={notify}
                        view
                      >
                        <Eye size={14} />
                        View
                      </ApiLink>
                    )}
                </div>
                {profile.colorSpace === "cmyk" && (
                  <small className="muted">
                    CMYK output. Printer-specific color matching and PDF/X
                    qualification require a production color profile.
                  </small>
                )}
                {scope === renderKey &&
                  job.state === "complete" &&
                  job.artifact.mime === "application/pdf" &&
                  job.artifact.downloadAllowed && (
                    <RetainHandoff
                      key={`${job.id}:${versionId || "demo"}`}
                      jobId={job.id}
                      versionId={versionId}
                      projectName={p.name}
                      accepted={accepted}
                      initials={initials}
                      comments={comments}
                      notify={notify}
                    />
                  )}
              </>
            )}
          </div>
        ))}
        <div className="proof-info">
          <FileCheck2 size={19} />
          <strong>A proof you can trust</strong>
          <p>
            Your document is generated from the same field values and layout
            you’re reviewing.
          </p>
        </div>
      </aside>
      <main className="proof-stage" inert={modalForm && formOpen}>
        <div className="proof-toolbar">
          <div className="live-label">
            <span className="live-dot" />
            Live proof<span className="badge">RGB preview</span>
          </div>
          <div className="tool-cluster">
            <button
              aria-label="Previous page"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft size={16} />
            </button>
            <span>
              {Math.min(page + 1, scene.pages.length)} / {scene.pages.length}
            </span>
            <button
              aria-label="Next page"
              disabled={page >= scene.pages.length - 1}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight size={16} />
            </button>
            {p.settings.bleedPreview?.enabled !== false && (
              <label className="bleed-preview-toggle">
                <input
                  type="checkbox"
                  checked={showBleed}
                  onChange={(e) => setShowBleed(e.target.checked)}
                />
                Show bleed
                {showBleed && !bleedConfigured && (
                  <small className="muted"> · none set</small>
                )}
              </label>
            )}
            <Select
              aria-label="Proof zoom"
              value={zoomChoice}
              onChange={(e) =>
                setZoom(
                  e.target.value === "fit" ? "fit" : Number(e.target.value),
                )
              }
            >
              <option value="fit">Fit</option>
              {[0.5, 0.75, 0.9, 1, 1.25, 1.5].map((z) => (
                <option key={z} value={z}>
                  {Math.round(z * 100)}%
                </option>
              ))}
            </Select>
            <button
              aria-label="Fullscreen proof"
              onClick={() => setShowFull(true)}
            >
              <Maximize2 size={16} />
            </button>
          </div>
        </div>
        <div
          className={`proof-artboard ${zoomChoice === "fit" ? "fit" : ""}`}
          ref={artboard}
        >
          <ProofCanvas
            {...transport.proofServices}
            project={p}
            context={context}
            values={values}
            partId={partId || undefined}
            pageIndex={page}
            profile={previewProfile}
            showProduction={showBleed}
            zoom={zoom}
          />
          <div className="preview-caption">
            <span>
              {scene.pages[Math.min(page, scene.pages.length - 1)].name}
            </span>
            <span>
              {scene.pages[
                Math.min(page, scene.pages.length - 1)
              ].width.toFixed(0)}{" "}
              ×{" "}
              {scene.pages[
                Math.min(page, scene.pages.length - 1)
              ].height.toFixed(0)}{" "}
              pt
            </span>
          </div>
        </div>
        <div className="proof-bottom-bar">
          <CheckCircle2 size={14} />
          <span>Changes update in real time</span>
          <span>Fonts and geometry shared with export</span>
        </div>
      </main>
      {duplicate?.scope === renderKey && (
        <GenerationConflictDialog
          profileName={duplicate.value.profileName}
          onClose={() => setDuplicate(undefined)}
          onReplace={() => void submit(duplicate.value.id)}
        />
      )}
      {showSaved && (
        <Modal title="Saved proofs" onClose={() => setShowSaved(false)}>
          {proofs.length ? (
            proofs.map((proof) => (
              <article className="saved-proof-row" key={proof.id}>
                <div>
                  <strong>{proof.name}</strong>
                  <small>
                    {new Date(proof.at).toLocaleString()} ·{" "}
                    {proof.accepted ? "Approved" : "Draft proof"}
                  </small>
                </div>
                <Button
                  onClick={() => {
                    onValues(proof.values);
                    setProfileSelections(proof.profileSelections || {});
                    host.setSourceParameters(proof.sourceParameters || {});
                    onPart(proof.partId || "");
                    setComments(proof.comments);
                    setInitials(proof.initials);
                    setAccepted(
                      proof.accepted &&
                        proof.contextHash === currentContext?.hash &&
                        proof.partId === (partId || undefined),
                    );
                    setShowSaved(false);
                  }}
                >
                  Restore
                </Button>
              </article>
            ))
          ) : (
            <p>No saved proofs yet.</p>
          )}
        </Modal>
      )}
      {showFull && (
        <Modal title="Document proof" wide onClose={() => setShowFull(false)}>
          <div className="fullscreen-proof">
            <ProofCanvas
              {...transport.proofServices}
              project={p}
              context={context}
              values={values}
              partId={partId || undefined}
              pageIndex={page}
              profile={previewProfile}
              showProduction={showBleed}
            />
          </div>
        </Modal>
      )}
      {showProfile && (
        <ProfileDialog
          sourceParameters={host.sourceParameters}
          onSourceParametersChange={host.setSourceParameters}
          key={`${p.id}:${versionId || "draft"}:${showProfile}`}
          project={p}
          instanceId={showProfile}
          versionId={versionId}
          onClose={() => setShowProfile(undefined)}
          onApply={(v) => {
            onValues(
              applyFormProfile(
                p,
                values,
                showProfile,
                v,
                partId || undefined,
                context,
              ),
            );
            setShowProfile(undefined);
          }}
        />
      )}
    </div>
  );
}
function ProfileDialog(props: import("./FormProfiles").FormProfilesProps) {
  const transport = useWorkspaceTransport();
  const instance = props.project.formInstances.find(
    (item) => item.id === props.instanceId,
  );
  const form = props.project.forms.find((item) => item.id === instance?.formId);
  return transport.demo && !form?.profileLibrary ? (
    <OfflineProfileDialog {...props} />
  ) : (
    <NativeFormProfiles {...props} />
  );
}
function OfflineProfileDialog({
  project,
  instanceId,
  onClose,
  onApply,
}: {
  project: Project;
  instanceId: string;
  onClose: () => void;
  onApply: (v: Values) => void;
}) {
  const transport = useWorkspaceTransport();
  const i = project.formInstances.find((i) => i.id === instanceId)!,
    form = project.forms.find((f) => f.id === i.formId)!,
    [values, setValues] = useState<Values>({}),
    [name, setName] = useState(""),
    [profileError, setProfileError] = useState(""),
    key = `proof-form-profile-${form.id}`;
  const [saved, setSaved] = useState<{ name: string; values: Values }[]>(() => {
    try {
      return transport.demo
        ? JSON.parse(localStorage.getItem(key) || "[]")
        : transport.profiles.load(key);
    } catch {
      return [];
    }
  });
  const definition = {
    ...project,
    fields: form.fields,
    groups: [],
    tabs: [],
    triggers: [],
    parts: [],
    formInstances: [],
    pages: project.pages.map((p) => ({ ...p, nodes: [] })),
  };
  return (
    <Modal title={i.label} onClose={onClose}>
      <FormGenerator
        loadAsset={transport.loadAsset}
        transformImage={transport.proofServices.transformImage}
        project={definition}
        values={values}
        onChange={setValues}
        compact
      />
      <Control label="Saved form profile">
        <Select
          value=""
          onChange={(e) => setValues(saved[Number(e.target.value)].values)}
        >
          <option value="">Choose a profile</option>
          {saved.map((p, i) => (
            <option key={i} value={i}>
              {p.name}
            </option>
          ))}
        </Select>
      </Control>
      {form.allowCreate && (
        <div className="inline-form">
          <input
            aria-label="Form profile name"
            placeholder="Profile name"
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
          />
          <Button
            disabled={!name.trim()}
            onClick={() => {
              const profiles = [
                ...saved.filter((p) => p.name !== name),
                { name, values },
              ];
              try {
                if (transport.demo)
                  localStorage.setItem(key, JSON.stringify(profiles));
                else transport.profiles.save(key, profiles);
                setSaved(profiles);
                setProfileError("");
                setName("");
              } catch (error) {
                setProfileError((error as Error).message);
              }
            }}
          >
            {transport.demo ? "Save profile" : "Keep in this session"}
          </Button>
        </div>
      )}
      <Button kind="primary" onClick={() => onApply(values)}>
        Apply to this instance
      </Button>
      {profileError && <p role="alert">{profileError}</p>}
    </Modal>
  );
}
